// Emergency Cancellation Request Form for Clients
import React, { useState, useEffect } from 'react';
import {
  AlertTriangle,
  Calendar,
  Clock,
  X,
  ChevronLeft,
  ChevronRight,
  Phone,
  CheckCircle,
  Loader
} from 'lucide-react';
import {
  CancellationUrgency,
  CancellationReasonCategory,
  CancellationRequestFormData,
  CANCELLATION_REASON_LABELS,
  URGENCY_CONFIG
} from '../../types/emergencyCancellation';
import FirebaseEmergencyCancellationService from '../../services/firebase/FirebaseEmergencyCancellationService';
import { firebaseAppointmentService, ProviderAvailability, TimeSlot } from '../../services/firebase/FirebaseAppointmentService';
import EmailNotificationService from '../../services/EmailNotificationService';
import VoiceInputButton from '../VoiceInputButton';
import FirebaseActivityService from '../../services/firebase/FirebaseActivityService';

interface EmergencyCancellationFormProps {
  appointment: {
    id: string;
    date: string;
    time: string;
    type: 'video' | 'in-person';
    providerName: string;
    providerId: string;
  };
  client: {
    id: string;
    name: string;
    email?: string;
    phone?: string;
  };
  onClose: () => void;
  onSuccess: () => void;
}

const EmergencyCancellationForm: React.FC<EmergencyCancellationFormProps> = ({
  appointment,
  client,
  onClose,
  onSuccess
}) => {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [urgency, setUrgency] = useState<CancellationUrgency>('same-day');
  const [reasonCategory, setReasonCategory] = useState<CancellationReasonCategory | ''>('');
  const [reasonDetails, setReasonDetails] = useState('');
  const [wantsToReschedule, setWantsToReschedule] = useState(true);
  const [preferredDate, setPreferredDate] = useState('');
  const [preferredTime, setPreferredTime] = useState('');
  const [rescheduleNotes, setRescheduleNotes] = useState('');

  const cancellationService = FirebaseEmergencyCancellationService.getInstance();

  // Provider availability state
  const [providerAvailability, setProviderAvailability] = useState<ProviderAvailability[]>([]);
  const [availableSlots, setAvailableSlots] = useState<TimeSlot[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);

  // Fetch provider availability when reschedule is selected
  useEffect(() => {
    if (!wantsToReschedule) return;

    const loadAvailability = async () => {
      try {
        const avail = await firebaseAppointmentService.getProviderAvailability(appointment.providerId);
        setProviderAvailability(avail.filter(a => a.isActive));
      } catch (err) {
        console.error('Failed to load provider availability:', err);
      }
    };

    loadAvailability();
  }, [wantsToReschedule, appointment.providerId]);

  // Fetch available slots when date is selected
  useEffect(() => {
    if (!preferredDate) {
      setAvailableSlots([]);
      return;
    }

    const loadSlots = async () => {
      setLoadingSlots(true);
      setPreferredTime('');
      try {
        const slots = await firebaseAppointmentService.getAvailableSlots(appointment.providerId, new Date(preferredDate + 'T00:00:00'));
        setAvailableSlots(slots.filter(s => s.available));
      } catch (err) {
        console.error('Failed to load available slots:', err);
        setAvailableSlots([]);
      } finally {
        setLoadingSlots(false);
      }
    };

    loadSlots();
  }, [preferredDate, appointment.providerId]);

  // Helper: get available day names
  const getAvailableDayNames = (): string => {
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return providerAvailability
      .sort((a, b) => a.dayOfWeek - b.dayOfWeek)
      .map(a => dayNames[a.dayOfWeek])
      .join(', ');
  };

  // Helper: format time to 12-hour
  const formatTime12Hour = (time: string): string => {
    const [h, m] = time.split(':').map(Number);
    const period = h >= 12 ? 'PM' : 'AM';
    const hour = h % 12 || 12;
    return `${hour}:${m.toString().padStart(2, '0')} ${period}`;
  };

  // Calendar state
  const [calendarMonth, setCalendarMonth] = useState(() => new Date());
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const getDaysInMonth = (date: Date): Date[] => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days: Date[] = [];
    for (let i = firstDay.getDay() - 1; i >= 0; i--) {
      days.push(new Date(year, month, -i));
    }
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push(new Date(year, month, i));
    }
    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
      days.push(new Date(year, month + 1, i));
    }
    return days;
  };

  const hasAvailability = (date: Date): boolean => {
    if (providerAvailability.length === 0) return false;
    const dayOfWeek = date.getDay();
    return !!providerAvailability.find(a => a.dayOfWeek === dayOfWeek && a.isActive);
  };

  const isPastDate = (date: Date): boolean => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    const check = new Date(date);
    check.setHours(0, 0, 0, 0);
    return check < tomorrow;
  };

  const handleCalendarDateSelect = (date: Date) => {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    setPreferredDate(`${yyyy}-${mm}-${dd}`);
  };

  // Validate current step
  const isStep1Valid = urgency && reasonCategory;
  const isStep2Valid = reasonDetails.trim().length >= 10;
  const isStep3Valid = !wantsToReschedule || (preferredDate && preferredTime);

  // Handle form submission
  const handleSubmit = async () => {
    if (!reasonCategory) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const formData: CancellationRequestFormData = {
        appointmentId: appointment.id,
        urgency,
        reasonCategory,
        reasonDetails,
        wantsToReschedule,
        preferredRescheduleDate: wantsToReschedule ? preferredDate : undefined,
        preferredRescheduleTime: wantsToReschedule ? preferredTime : undefined,
        rescheduleNotes: wantsToReschedule ? rescheduleNotes : undefined
      };

      await cancellationService.submitCancellationRequest(formData, {
        appointmentDate: appointment.date,
        appointmentTime: appointment.time,
        appointmentType: appointment.type,
        clientId: client.id,
        clientName: client.name,
        clientEmail: client.email,
        clientPhone: client.phone,
        providerId: appointment.providerId,
        providerName: appointment.providerName
      });

      // Send email notification to client (non-blocking — don't fail the form)
      if (client.email) {
        EmailNotificationService.getInstance().sendCancellationNotification({
          clientEmail: client.email,
          clientName: client.name,
          providerName: appointment.providerName,
          appointmentDate: appointment.date,
          wantsToReschedule,
        }).catch(() => { /* silent — email is best-effort */ });
      }

      // Log activity for provider dashboard
      FirebaseActivityService.getInstance().logActivity({
        providerId: appointment.providerId,
        clientId: client.id,
        clientName: client.name,
        activityType: 'emergency_cancellation',
        title: 'Emergency cancellation',
        description: `${client.name} submitted an emergency cancellation for ${appointment.date}.`,
        metadata: { appointmentId: appointment.id, cancellationReason: `${urgency} - ${reasonCategory}` }
      }).catch(err => console.warn('[Activity] Cancellation log failed:', err));

      setIsSubmitted(true);
      setTimeout(() => {
        onSuccess();
      }, 2000);
    } catch (err) {
      console.error('Error submitting cancellation:', err);
      setError('Failed to submit cancellation request. Please try again or call your provider directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Success screen
  if (isSubmitted) {
    return (
      <div style={styles.container}>
        <div style={styles.successContainer}>
          <div style={styles.successIcon}>
            <CheckCircle size={48} color="#22c55e" />
          </div>
          <h2 style={styles.successTitle}>Request Submitted</h2>
          <p style={styles.successMessage}>
            Your emergency cancellation request has been sent to {appointment.providerName}.
            They will be notified immediately and will respond as soon as possible.
          </p>
          <p style={styles.successNote}>
            You will receive a confirmation when your request is processed.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerIcon}>
          <AlertTriangle size={24} color="#dc3545" />
        </div>
        <div>
          <h2 style={styles.title}>Emergency Cancellation</h2>
          <p style={styles.subtitle}>
            {appointment.date} at {appointment.time} with {appointment.providerName}
          </p>
        </div>
        <button
          onClick={onClose}
          style={styles.closeButton}
          aria-label="Close"
        >
          <X size={24} />
        </button>
      </div>

      {/* Crisis Notice */}
      <div style={styles.crisisNotice}>
        <Phone size={18} />
        <span>
          If this is a mental health emergency, please call <strong>988</strong> or go to your nearest emergency room.
        </span>
      </div>

      {/* Progress Steps */}
      <div style={styles.progressBar}>
        {[1, 2, 3].map((s) => (
          <div
            key={s}
            style={{
              ...styles.progressStep,
              ...(step >= s ? styles.progressStepActive : {})
            }}
          >
            {s}
          </div>
        ))}
      </div>

      {/* Step Content */}
      <div style={styles.content}>
        {/* Step 1: Urgency and Reason Category */}
        {step === 1 && (
          <div style={styles.stepContent}>
            <h3 style={styles.stepTitle}>How urgent is this cancellation?</h3>

            <div style={styles.urgencyOptions}>
              {(Object.entries(URGENCY_CONFIG) as [CancellationUrgency, typeof URGENCY_CONFIG['immediate']][]).map(
                ([key, config]) => (
                  <button
                    key={key}
                    onClick={() => setUrgency(key)}
                    style={{
                      ...styles.urgencyOption,
                      borderColor: urgency === key ? config.color : '#334155',
                      backgroundColor: urgency === key ? config.bgColor : 'transparent'
                    }}
                  >
                    <div
                      style={{
                        ...styles.urgencyDot,
                        backgroundColor: config.color
                      }}
                    />
                    <span style={{ color: urgency === key ? config.color : '#f8fafc' }}>
                      {config.label}
                    </span>
                  </button>
                )
              )}
            </div>

            <h3 style={{ ...styles.stepTitle, marginTop: '24px' }}>
              What is the reason for cancellation?
            </h3>

            <div style={styles.reasonOptions}>
              {(Object.entries(CANCELLATION_REASON_LABELS) as [CancellationReasonCategory, string][]).map(
                ([key, label]) => (
                  <button
                    key={key}
                    onClick={() => setReasonCategory(key)}
                    style={{
                      ...styles.reasonOption,
                      borderColor: reasonCategory === key ? '#3b82f6' : '#334155',
                      backgroundColor: reasonCategory === key ? 'rgba(59, 130, 246, 0.1)' : 'transparent'
                    }}
                  >
                    {label}
                  </button>
                )
              )}
            </div>
          </div>
        )}

        {/* Step 2: Details */}
        {step === 2 && (
          <div style={styles.stepContent}>
            <h3 style={styles.stepTitle}>Please provide more details</h3>
            <p style={styles.stepDescription}>
              This information helps your provider understand your situation and respond appropriately.
            </p>

            <label htmlFor="ec-reasonDetails" className="sr-only" style={{ position: 'absolute', width: '1px', height: '1px', overflow: 'hidden', clip: 'rect(0,0,0,0)' }}>
              Please provide more details
            </label>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
              <textarea
                id="ec-reasonDetails"
                value={reasonDetails}
                onChange={(e) => setReasonDetails(e.target.value)}
                placeholder="Please describe the circumstances that require you to cancel this appointment..."
                style={{ ...styles.textarea, flex: 1 }}
                rows={5}
                aria-label="Cancellation reason details"
              />
              <VoiceInputButton onResult={(text) => setReasonDetails(prev => prev ? prev + ' ' + text : text)} size={20} />
            </div>
            <p style={styles.charCount}>
              {reasonDetails.length} characters (minimum 10 required)
            </p>
          </div>
        )}

        {/* Step 3: Rescheduling */}
        {step === 3 && (
          <div style={styles.stepContent}>
            <h3 style={styles.stepTitle}>Would you like to reschedule?</h3>

            <div style={styles.rescheduleToggle}>
              <button
                onClick={() => setWantsToReschedule(true)}
                style={{
                  ...styles.toggleButton,
                  ...(wantsToReschedule ? styles.toggleButtonActive : {})
                }}
              >
                Yes, reschedule
              </button>
              <button
                onClick={() => setWantsToReschedule(false)}
                style={{
                  ...styles.toggleButton,
                  ...(!wantsToReschedule ? styles.toggleButtonActive : {})
                }}
              >
                No, just cancel
              </button>
            </div>

            {wantsToReschedule && (
              <div style={styles.rescheduleOptions}>
                <p style={styles.stepDescription}>
                  Select a date and available time to reschedule with {appointment.providerName}:
                </p>

                {/* Custom Calendar */}
                <div style={{
                  backgroundColor: '#1e293b',
                  borderRadius: '8px',
                  padding: '16px',
                  marginTop: '8px'
                }}>
                  {/* Month Navigation */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '12px'
                  }}>
                    <button
                      onClick={() => {
                        const prev = new Date(calendarMonth);
                        prev.setMonth(prev.getMonth() - 1);
                        setCalendarMonth(prev);
                      }}
                      style={{
                        background: 'none', border: 'none', cursor: 'pointer',
                        color: '#d1d5db', padding: '4px', display: 'flex'
                      }}
                    >
                      <ChevronLeft size={20} />
                    </button>
                    <h4 style={{ margin: 0, color: '#f9fafb', fontSize: '16px', fontWeight: '600' }}>
                      {calendarMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                    </h4>
                    <button
                      onClick={() => {
                        const next = new Date(calendarMonth);
                        next.setMonth(next.getMonth() + 1);
                        setCalendarMonth(next);
                      }}
                      style={{
                        background: 'none', border: 'none', cursor: 'pointer',
                        color: '#d1d5db', padding: '4px', display: 'flex'
                      }}
                    >
                      <ChevronRight size={20} />
                    </button>
                  </div>

                  {/* Weekday Headers */}
                  <div style={{
                    display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)',
                    gap: '4px', marginBottom: '4px'
                  }}>
                    {weekDays.map(day => (
                      <div key={day} style={{
                        textAlign: 'center', fontSize: '11px', fontWeight: 'bold',
                        color: '#d1d5db', padding: '4px'
                      }}>
                        {day}
                      </div>
                    ))}
                  </div>

                  {/* Days Grid */}
                  <div style={{
                    display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px'
                  }}>
                    {getDaysInMonth(calendarMonth).map((date, idx) => {
                      const isAvailable = hasAvailability(date);
                      const isPast = isPastDate(date);
                      const isCurrentMonthDay = date.getMonth() === calendarMonth.getMonth();
                      const isClickable = isAvailable && !isPast && isCurrentMonthDay;
                      const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
                      const isSelected = preferredDate === dateStr;

                      let bgColor = '#28527A';
                      if (isPast) bgColor = '#1f2937';
                      else if (isSelected) bgColor = '#6CA6C1';
                      else if (isAvailable && isCurrentMonthDay) bgColor = '#065f46';
                      else if (isCurrentMonthDay) bgColor = '#374151';

                      return (
                        <div
                          key={idx}
                          onClick={() => isClickable && handleCalendarDateSelect(date)}
                          style={{
                            minHeight: '40px', padding: '6px',
                            backgroundColor: bgColor, borderRadius: '4px',
                            cursor: isClickable ? 'pointer' : 'default',
                            opacity: isCurrentMonthDay ? 1 : 0.3,
                            border: isSelected ? '2px solid #1e293b' : 'none',
                            display: 'flex', flexDirection: 'column',
                            alignItems: 'center', justifyContent: 'center',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => {
                            if (isClickable && !isSelected) {
                              e.currentTarget.style.backgroundColor = '#047857';
                              e.currentTarget.style.transform = 'scale(1.05)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isSelected) {
                              e.currentTarget.style.backgroundColor = bgColor;
                            }
                            e.currentTarget.style.transform = 'scale(1)';
                          }}
                        >
                          <div style={{
                            fontSize: '13px', color: isSelected ? '#1e293b' : '#f9fafb',
                            fontWeight: isSelected ? 'bold' : 'normal'
                          }}>
                            {date.getDate()}
                          </div>
                          {isAvailable && isCurrentMonthDay && !isPast && (
                            <div style={{ fontSize: '10px', marginTop: '2px' }}>✓</div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Legend */}
                  <div style={{
                    display: 'flex', gap: '12px', marginTop: '12px',
                    fontSize: '11px', color: '#d1d5db', justifyContent: 'center'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <div style={{ width: '12px', height: '12px', backgroundColor: '#065f46', borderRadius: '3px' }} />
                      <span>Available</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <div style={{ width: '12px', height: '12px', backgroundColor: '#374151', borderRadius: '3px' }} />
                      <span>Unavailable</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <div style={{ width: '12px', height: '12px', backgroundColor: '#6CA6C1', borderRadius: '3px' }} />
                      <span>Selected</span>
                    </div>
                  </div>
                </div>

                {preferredDate && (
                  <div style={{ ...styles.inputGroup, marginTop: '16px' }}>
                    <label style={styles.inputLabel}>
                      <Clock size={16} /> Available Times
                    </label>

                    {loadingSlots ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px', color: '#94a3b8' }}>
                        <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} />
                        <span>Loading available times...</span>
                      </div>
                    ) : availableSlots.length > 0 ? (
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
                        gap: '8px',
                        marginTop: '8px'
                      }}>
                        {availableSlots.map((slot) => (
                          <button
                            key={slot.startTime}
                            onClick={() => setPreferredTime(slot.startTime)}
                            style={{
                              padding: '10px 8px',
                              borderRadius: '8px',
                              border: preferredTime === slot.startTime ? '2px solid #3b82f6' : '1px solid #334155',
                              backgroundColor: preferredTime === slot.startTime ? 'rgba(59, 130, 246, 0.15)' : '#1e293b',
                              color: preferredTime === slot.startTime ? '#3b82f6' : '#f8fafc',
                              cursor: 'pointer',
                              fontSize: '14px',
                              fontWeight: preferredTime === slot.startTime ? '600' : '400',
                              textAlign: 'center'
                            }}
                          >
                            {formatTime12Hour(slot.startTime)}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <p style={{ margin: '8px 0 0 0', fontSize: '13px', color: '#f59e0b' }}>
                        No available times on this date. Please try another day.
                      </p>
                    )}
                  </div>
                )}

                <div style={{ ...styles.inputGroup, marginTop: '16px' }}>
                  <label htmlFor="ec-rescheduleNotes" style={styles.inputLabel}>Additional Notes (optional)</label>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                    <textarea
                      id="ec-rescheduleNotes"
                      value={rescheduleNotes}
                      onChange={(e) => setRescheduleNotes(e.target.value)}
                      placeholder="Any specific scheduling needs or preferences..."
                      style={{ ...styles.textarea, flex: 1 }}
                      rows={2}
                    />
                    <VoiceInputButton onResult={(text) => setRescheduleNotes(prev => prev ? prev + ' ' + text : text)} size={18} />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div style={styles.errorMessage}>
          <AlertTriangle size={16} />
          {error}
        </div>
      )}

      {/* Footer Actions */}
      <div style={styles.footer}>
        {step > 1 && (
          <button
            onClick={() => setStep((step - 1) as 1 | 2)}
            style={styles.backButton}
          >
            Back
          </button>
        )}

        {step < 3 ? (
          <button
            onClick={() => setStep((step + 1) as 2 | 3)}
            disabled={step === 1 ? !isStep1Valid : !isStep2Valid}
            style={{
              ...styles.nextButton,
              ...(!(step === 1 ? isStep1Valid : isStep2Valid)
                ? styles.buttonDisabled
                : {})
            }}
          >
            Continue
            <ChevronRight size={18} />
          </button>
        ) : (
          <button
            onClick={handleSubmit}
            disabled={!isStep3Valid || isSubmitting}
            style={{
              ...styles.submitButton,
              ...(!isStep3Valid || isSubmitting ? styles.buttonDisabled : {})
            }}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Cancellation Request'}
          </button>
        )}
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    backgroundColor: '#0f172a',
    borderRadius: '16px',
    border: '1px solid #334155',
    maxWidth: '600px',
    width: '100%',
    maxHeight: '90vh',
    overflow: 'auto',
    display: 'flex',
    flexDirection: 'column'
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '14px',
    padding: '20px 24px',
    borderBottom: '1px solid #334155'
  },
  headerIcon: {
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    backgroundColor: 'rgba(220, 53, 69, 0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0
  },
  title: {
    margin: 0,
    fontSize: '18px',
    fontWeight: '600',
    color: '#f8fafc'
  },
  subtitle: {
    margin: '4px 0 0 0',
    fontSize: '13px',
    color: '#64748b'
  },
  closeButton: {
    background: 'none',
    border: 'none',
    padding: '8px',
    cursor: 'pointer',
    color: '#64748b',
    marginLeft: 'auto',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  crisisNotice: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '12px 24px',
    backgroundColor: 'rgba(220, 53, 69, 0.1)',
    borderBottom: '1px solid #334155',
    fontSize: '13px',
    color: '#fca5a5'
  },
  progressBar: {
    display: 'flex',
    justifyContent: 'center',
    gap: '8px',
    padding: '16px 24px'
  },
  progressStep: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    backgroundColor: '#1e293b',
    border: '2px solid #334155',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    fontWeight: '600',
    color: '#64748b'
  },
  progressStepActive: {
    backgroundColor: '#3b82f6',
    borderColor: '#3b82f6',
    color: '#fff'
  },
  content: {
    flex: 1,
    padding: '0 24px 24px',
    overflow: 'auto'
  },
  stepContent: {
    display: 'flex',
    flexDirection: 'column'
  },
  stepTitle: {
    margin: '0 0 16px 0',
    fontSize: '16px',
    fontWeight: '600',
    color: '#f8fafc'
  },
  stepDescription: {
    margin: '0 0 16px 0',
    fontSize: '14px',
    color: '#94a3b8'
  },
  urgencyOptions: {
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap'
  },
  urgencyOption: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 16px',
    borderRadius: '10px',
    border: '2px solid',
    background: 'none',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500'
  },
  urgencyDot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%'
  },
  reasonOptions: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
    gap: '10px'
  },
  reasonOption: {
    padding: '12px 14px',
    borderRadius: '8px',
    border: '1px solid',
    background: 'none',
    cursor: 'pointer',
    fontSize: '13px',
    color: '#f8fafc',
    textAlign: 'left'
  },
  textarea: {
    width: '100%',
    padding: '12px',
    borderRadius: '8px',
    border: '1px solid #334155',
    backgroundColor: '#1e293b',
    color: '#f8fafc',
    fontSize: '14px',
    resize: 'vertical',
    fontFamily: 'inherit'
  },
  charCount: {
    margin: '8px 0 0 0',
    fontSize: '12px',
    color: '#64748b',
    textAlign: 'right'
  },
  rescheduleToggle: {
    display: 'flex',
    gap: '12px',
    marginBottom: '20px'
  },
  toggleButton: {
    flex: 1,
    padding: '14px',
    borderRadius: '10px',
    border: '2px solid #334155',
    backgroundColor: 'transparent',
    color: '#94a3b8',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer'
  },
  toggleButtonActive: {
    borderColor: '#3b82f6',
    backgroundColor: 'rgba(59, 130, 246, 0.1)',
    color: '#3b82f6'
  },
  rescheduleOptions: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  },
  dateTimeRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px'
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  inputLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '13px',
    fontWeight: '500',
    color: '#94a3b8'
  },
  input: {
    padding: '12px',
    borderRadius: '8px',
    border: '1px solid #334155',
    backgroundColor: '#1e293b',
    color: '#f8fafc',
    fontSize: '14px',
    width: '100%',
    colorScheme: 'dark' as const,
    cursor: 'pointer'
  },
  errorMessage: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 24px',
    backgroundColor: 'rgba(220, 53, 69, 0.1)',
    color: '#fca5a5',
    fontSize: '13px'
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '12px',
    padding: '16px 24px',
    borderTop: '1px solid #334155'
  },
  backButton: {
    padding: '12px 20px',
    borderRadius: '8px',
    border: '1px solid #334155',
    backgroundColor: 'transparent',
    color: '#94a3b8',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer'
  },
  nextButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '12px 24px',
    borderRadius: '8px',
    border: 'none',
    backgroundColor: '#3b82f6',
    color: '#fff',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    marginLeft: 'auto'
  },
  submitButton: {
    padding: '12px 24px',
    borderRadius: '8px',
    border: 'none',
    backgroundColor: '#dc3545',
    color: '#fff',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    marginLeft: 'auto'
  },
  buttonDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed'
  },
  successContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 40px',
    textAlign: 'center'
  },
  successIcon: {
    marginBottom: '20px'
  },
  successTitle: {
    margin: '0 0 12px 0',
    fontSize: '20px',
    fontWeight: '600',
    color: '#f8fafc'
  },
  successMessage: {
    margin: '0 0 16px 0',
    fontSize: '14px',
    color: '#94a3b8',
    maxWidth: '400px'
  },
  successNote: {
    margin: 0,
    fontSize: '13px',
    color: '#64748b'
  }
};

export default EmergencyCancellationForm;
